import { PropsWithChildren } from 'react';
import { useTextSelection } from '../features/Highlighting/hooks/useTextSelection';
import { Stack } from '@mui/material';
import ChapterNavigationFooter from './ChapterNavigation/Footer';
import { useReaderConfiguration } from '../features/ReaderConfiguration/ReaderConfigurationContext';
import {
  ReaderContextProvider,
  useReaderContext,
} from '../features/ContentRendering/components/ReaderContext';
import {
  AnnotationToolbarProvider,
  useAnnotationToolbarContext,
} from '../features/Annotation/AnnotationToolbarContext';
import {
  getFontSizeMultiplier,
  getParagraphSpacingMultiplier,
  getContentWidthCharacterCount,
  getLetterSpacing,
  getWordSpacing,
  getLineHeight,
} from '../features/ReaderConfiguration/utils/ConfigUtils';
import { useSessionContext } from './SessionContext';

export default function ContentContainerWrapper({
  children,
}: Readonly<PropsWithChildren>) {
  return (
    <ReaderContextProvider>
      <AnnotationToolbarProvider>
        <ContentContainer>{children}</ContentContainer>
      </AnnotationToolbarProvider>
    </ReaderContextProvider>
  );
}

export function ContentContainer({ children }: Readonly<PropsWithChildren>) {
  const { config } = useReaderConfiguration();
  const { htmlRange } = useSessionContext();
  const { state } = useReaderContext();
  const { handleTextSelection } = useTextSelection();
  const { closeToolbar, isToolbarOpen } = useAnnotationToolbarContext();

  return (
    <Stack
      id='content-container'
      direction='column'
      alignItems='center'
      data-highlighting-container
      onMouseDown={() => {
        // Close toolbar immediately when user starts new selection
        if (isToolbarOpen) {
          closeToolbar();
        }
      }}
      onMouseUp={() => {
        if (config.showHighlights) {
          handleTextSelection();
        }
      }}
      style={
        {
          '--paragraph-spacing-multiplier':
            getParagraphSpacingMultiplier(config),
          '--font-size-multiplier': getFontSizeMultiplier(config),
          '--width-in-characters': getContentWidthCharacterCount(config),
          '--line-height': getLineHeight(config),
          '--letter-spacing': getLetterSpacing(config),
          '--word-spacing': getWordSpacing(config),
        } as React.CSSProperties
      }
    >
      <Stack
        alignItems='center'
        sx={{ width: '100%', paddingX: { xs: 2, md: 4, lg: 8 } }}
      >
        <Stack id='content-area'>{children}</Stack>
      </Stack>
      {htmlRange !== null && state === 'ready' && (
        <Stack
          id='chapter-navigation'
          alignItems='center'
          sx={{
            width: '100%',
            paddingTop: 3,
            paddingBottom: { xs: 0, sm: 8 },
            paddingX: { xs: 0, sm: 2, md: 4, lg: 8 },
          }}
        >
          <ChapterNavigationFooter />
        </Stack>
      )}
    </Stack>
  );
}
